import { FC, HTMLAttributes, useEffect } from 'react';
import FocusTrap from 'focus-trap-react';
import { ModalWrapper, ModalContent, CloseButton } from './Modal.styles';
import { Cross } from '../../icons';

export interface ModalProps extends HTMLAttributes<HTMLDivElement> {
  open: boolean;
  onClose: () => void;
  accessibleName?: string;
  children: React.ReactNode;
  closeButtonInsideModal?: boolean;
}

/**
 * A modal dialog that can be toggled open/closed.
 * The dialog is modal, meaning the user cannot interact with
 * the parent window until the dialog is closed.
 */
export const Modal: FC<ModalProps> = ({
  open,
  onClose,
  children,
  accessibleName = 'Dialog',
  closeButtonInsideModal = true,
  ...otherProps
}) => {
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  });

  if (!open) {
    return null;
  }

  const handleOverlayClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (event.target === event.currentTarget) {
      onClose();
    }
  };

  return (
    <FocusTrap>
      <ModalWrapper
        onClick={handleOverlayClick}
        $open={open}
        aria-label={accessibleName}
      >
        <ModalContent {...otherProps}>
          <CloseButton
            onClick={onClose}
            tabIndex={0}
            role="button"
            aria-label="Close this popup"
            title="Close this popup"
            $insideContent={closeButtonInsideModal}
          >
            <Cross />
          </CloseButton>
          {children}
        </ModalContent>
      </ModalWrapper>
    </FocusTrap>
  );
};

export default Modal;
